// Arrays and arranging: the rectangular and polar arrays (in the plan and in a
// section view), and Align / Distribute / Attach over the selection, on the plan
// or on a sheet. docs/claude/tools.md.
import {
  type Bounds,
  type SectionLine,
  type Vec2,
} from '../../shared/types';
import {
  MULTI_KINDS,
  selectedCount,
  selectedItems,
  selectedObjects,
} from '../selection';
import { selectionClipboard } from './clipboard';
import {
  flashToolStatus,
  invalidate,
  pushHistory,
  renderProperties,
  state,
} from './core';
import { drawingTab } from './drawingTab';
import { arrangeSheetObjects } from '../sheetObjects';
import { titleEditor } from './titleEditor';
import {
  frameMove,
  PLAN_PLANE,
  sectionOf,
  type WorkPlane,
} from '../workPlane';
import {
  applyTilt,
  cloneTargets,
  insertClipboard,
  moveSelectionOnce,
  planeRefusal,
  rotateAround,
  sectionSelectionBoxes,
  sectionSelectionMiddle,
  selectedMovables,
  selectionCenter,
  type SelectionMove,
  setObjectSelection,
  startTilt,
  syncDimensions,
  toolPlane,
} from '../main';
import {
  activeSheet,
  schemSelSet,
  SHEET_OBJECTS,
} from './sheetSpace';

// ---- Arrays, and align / distribute / attach ----
/** Rectangular array: fill a rows×cols grid, spacing `colSp` mm across and
 *  `rowSp` mm up, leaving the original in place (cell 0,0). */
export function arrayRect(rows: number, cols: number, rowSp: number, colSp: number): void {
  rows = Math.max(1, Math.round(rows)); cols = Math.max(1, Math.round(cols));
  if (rows * cols <= 1) return;
  if (!selectedItems(state, state.drawing).length) return;
  // Columns across and rows up the plane: in a drilled-into section view along
  // its cut and stacked up.
  const plane = toolPlane(), why = planeRefusal(plane);
  if (why) { flashToolStatus(why); return; }
  pushHistory();
  const cb = selectionClipboard();
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
    if (r === 0 && c === 0) continue;
    const { dx, dy, dz } = frameMove(plane, { x: c * colSp, y: r * rowSp });
    cloneTargets((p) => ({ x: p.x + dx, y: p.y + dy }), 0, dz, cb);
  }
  syncDimensions(); renderProperties(); invalidate();
}

/** Polar array: `count` total copies spread over `totalDeg` about the selection
 *  centre (the original counts as the first). */
export function arrayPolar(count: number, totalDeg: number): void {
  count = Math.max(1, Math.round(count));
  if (count <= 1) return;
  const sec = sectionOf(arrayPolarPlane());
  if (sec) { arrayPolarTilt(sec, count, totalDeg); return; }
  const pivot = drawingTab.arrayPolarCenter ?? selectionCenter(); // explicit centre, else selection centre
  if (!pivot) return;
  // A full 360° turn divides evenly by count; a partial sweep spans the ends.
  const full = ((totalDeg % 360) + 360) % 360 === 0;
  const step = full ? 360 / count : totalDeg / (count - 1);
  pushHistory();
  const cb = selectionClipboard();
  for (let k = 1; k < count; k++) {
    const deg = step * k;
    const rad = (deg * Math.PI) / 180;
    cloneTargets((p) => rotateAround(p, pivot, rad), deg, 0, cb);
  }
  syncDimensions(); renderProperties(); invalidate();
}

/** The plane a polar array works in. Its centre is its base point, which
 *  decides: picked in a section view, that view's (the copies tilt); picked
 *  anywhere else, the plan. With none picked, the drilled-into view, else the
 *  plan. */
function arrayPolarPlane(): WorkPlane {
  if (!drawingTab.arrayPolarCenter) return toolPlane();
  const sec = drawingTab.arrayPolarSection ? (state.drawing.sections || []).find((s) => s.id === drawingTab.arrayPolarSection!.id) : undefined;
  return sec ? { kind: 'section', sec } : PLAN_PLANE;
}

/** A polar array in a section view: copies tilted about the level line square
 *  to the view through its centre (picked there, else the selection's middle in
 *  the view) — only of parts that tilt. */
function arrayPolarTilt(sec: SectionLine, count: number, totalDeg: number): void {
  const why = planeRefusal({ kind: 'section', sec }, true);
  if (why) { flashToolStatus(why); return; }
  const at = drawingTab.arrayPolarCenter && drawingTab.arrayPolarSection?.id === sec.id ? { ...drawingTab.arrayPolarCenter, z: drawingTab.arrayPolarSection.z } : sectionSelectionMiddle(sec);
  const cb = selectionClipboard();
  if (!at || !cb) return;
  const own = selectedObjects(state, MULTI_KINDS);
  const full = ((totalDeg % 360) + 360) % 360 === 0;
  const step = full ? 360 / count : totalDeg / (count - 1);
  pushHistory();
  for (let k = 1; k < count; k++) {
    insertClipboard(cb, { x: 0, y: 0 });
    applyTilt(startTilt(sec, at), step * k);
  }
  setObjectSelection(own);
  syncDimensions(); renderProperties(); invalidate();
}

/** Number of independently-movable items in the current selection (every object
 *  kind counts). Used to enable the Align / Distribute toolbar buttons. */
export function selectedItemCount(): number {
  if (activeSheet() && !titleEditor.titleEdit) return sheetArrangeTargets().length;
  return selectedCount(state, MULTI_KINDS);
}

/** A sheet's selected objects as Align / Distribute targets. Paper is Y-down, so
 *  the bounds are handed out with Y negated (and moves negated back): "top" then
 *  means the top of the page, and the model's routines run unchanged. A
 *  connection is not a target — its ends ride on the boxes it joins. */
function sheetArrangeTargets(): MoveTarget[] {
  const sh = activeSheet(); if (!sh || titleEditor.titleEdit) return [];
  // Paper is Y down, an arrange's frame Y up.
  return arrangeSheetObjects(sh, SHEET_OBJECTS, schemSelSet).map((t) => ({
    bounds: { min: { x: t.box.min.x, y: -t.box.max.y }, max: { x: t.box.max.x, y: -t.box.min.y } },
    move: (dx: number, dy: number) => t.move(dx, -dy),
  }));
}

/** A selected item's bounding box in the frame it is arranged in (the plan's,
 *  a section view's along / up, a sheet's) and how to shift it there: the Align
 *  / Distribute / Attach targets. */
interface MoveTarget { bounds: Bounds; move: (dx: number, dy: number) => void; }

/** The model's selection as targets: each object's box in the plan or in the
 *  drilled-into section view, `id` the object it moves. */
function modelBoxes(): { id: string; bounds: Bounds }[] {
  const bboxOf = (pts: Vec2[]): Bounds => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of pts) { if (p.x < minX) minX = p.x; if (p.y < minY) minY = p.y; if (p.x > maxX) maxX = p.x; if (p.y > maxY) maxY = p.y; }
    return { min: { x: minX, y: minY }, max: { x: maxX, y: maxY } };
  };
  const sec = sectionOf(toolPlane());
  if (sec) return sectionSelectionBoxes(sec);
  return selectedMovables().map((m) => ({ id: m.id, bounds: bboxOf(m.verts()) }));
}

/** Arrange the selection: `place` shifts each target, at least `min` of them.
 *  On a sheet, its objects; in the model, ONE move session (moveSession.ts) —
 *  each object shifted with what its room carries, re-anchored in the room it
 *  lands in, one undo step. */
function arrange(min: number, place: (targets: MoveTarget[]) => void): void {
  if (activeSheet() && !titleEditor.titleEdit) {
    const targets = sheetArrangeTargets();
    if (targets.length < min) return;
    pushHistory();
    place(targets);
    syncDimensions();
    renderProperties();
    invalidate();
    return;
  }
  const boxes = modelBoxes();
  if (boxes.length < min) return;
  const sec = sectionOf(toolPlane()), plane: WorkPlane = sec ? { kind: 'section', sec } : PLAN_PLANE;
  moveSelectionOnce((s: SelectionMove) => place(boxes.map(({ id, bounds }) => ({
    bounds,
    move: (dx, dy) => { const d = frameMove(plane, { x: dx, y: dy }); s.moveOne(id, { x: d.dx, y: d.dy, z: d.dz }); },
  }))));
}

/** Align every selected item's edge (or centre isn't offered — just the four
 *  edges) to the outermost matching edge of the whole selection. */
export function alignSelection(edge: 'left' | 'right' | 'top' | 'bottom'): void {
  arrange(2, (targets) => {
    if (edge === 'left') {
      const target = Math.min(...targets.map((t) => t.bounds.min.x));
      for (const t of targets) t.move(target - t.bounds.min.x, 0);
    } else if (edge === 'right') {
      const target = Math.max(...targets.map((t) => t.bounds.max.x));
      for (const t of targets) t.move(target - t.bounds.max.x, 0);
    } else if (edge === 'top') {
      // World Y-up: "top" is the max-Y edge.
      const target = Math.max(...targets.map((t) => t.bounds.max.y));
      for (const t of targets) t.move(0, target - t.bounds.max.y);
    } else {
      const target = Math.min(...targets.map((t) => t.bounds.min.y));
      for (const t of targets) t.move(0, target - t.bounds.min.y);
    }
  });
}

/** Space the selected items evenly along an axis: the two outermost items stay
 *  put, and the gaps between every item's bounding box are equalised. */
export function distributeSelection(axis: 'h' | 'v'): void {
  arrange(3, (targets) => {
    if (axis === 'h') {
      const sorted = [...targets].sort((a, b) => (a.bounds.min.x + a.bounds.max.x) - (b.bounds.min.x + b.bounds.max.x));
      const span = sorted[sorted.length - 1].bounds.max.x - sorted[0].bounds.min.x;
      const widths = sorted.reduce((s, t) => s + (t.bounds.max.x - t.bounds.min.x), 0);
      const gap = (span - widths) / (sorted.length - 1);
      let x = sorted[0].bounds.min.x;
      for (const t of sorted) {
        const w = t.bounds.max.x - t.bounds.min.x;
        t.move(x - t.bounds.min.x, 0);
        x += w + gap;
      }
    } else {
      // World Y-up: distribute top-to-bottom (descending Y) in natural reading order.
      const sorted = [...targets].sort((a, b) => (b.bounds.min.y + b.bounds.max.y) - (a.bounds.min.y + a.bounds.max.y));
      const span = sorted[0].bounds.max.y - sorted[sorted.length - 1].bounds.min.y;
      const heights = sorted.reduce((s, t) => s + (t.bounds.max.y - t.bounds.min.y), 0);
      const gap = (span - heights) / (sorted.length - 1);
      let y = sorted[0].bounds.max.y;
      for (const t of sorted) {
        const h = t.bounds.max.y - t.bounds.min.y;
        t.move(0, (y - h) - t.bounds.min.y);
        y -= h + gap;
      }
    }
  });
}

/** Pack the selected items edge-to-edge along one side: the object at the given
 *  side stays put and every other object is pulled in, in order from that side,
 *  so each one's leading edge meets the previous object's trailing edge (no gaps,
 *  no overlaps). "Left" packs the row leftward, "top" packs the column upward. */
export function attachSelection(side: 'left' | 'right' | 'top' | 'bottom'): void {
  arrange(2, (targets) => {
    const w = (t: MoveTarget): number => t.bounds.max.x - t.bounds.min.x;
    const h = (t: MoveTarget): number => t.bounds.max.y - t.bounds.min.y;
    if (side === 'left') {
      const s = [...targets].sort((a, b) => a.bounds.min.x - b.bounds.min.x);
      let edge = s[0].bounds.max.x;
      for (let i = 1; i < s.length; i++) { s[i].move(edge - s[i].bounds.min.x, 0); edge += w(s[i]); }
    } else if (side === 'right') {
      const s = [...targets].sort((a, b) => b.bounds.max.x - a.bounds.max.x);
      let edge = s[0].bounds.min.x;
      for (let i = 1; i < s.length; i++) { s[i].move(edge - s[i].bounds.max.x, 0); edge -= w(s[i]); }
    } else if (side === 'top') {
      // World Y-up: "top" is the max-Y edge; pack downward from the topmost object.
      const s = [...targets].sort((a, b) => b.bounds.max.y - a.bounds.max.y);
      let edge = s[0].bounds.min.y;
      for (let i = 1; i < s.length; i++) { s[i].move(0, edge - s[i].bounds.max.y); edge -= h(s[i]); }
    } else {
      const s = [...targets].sort((a, b) => a.bounds.min.y - b.bounds.min.y);
      let edge = s[0].bounds.max.y;
      for (let i = 1; i < s.length; i++) { s[i].move(0, edge - s[i].bounds.min.y); edge += h(s[i]); }
    }
  });
}
